import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { H3Configuration } from "@/types/dataset";
import { WidgetConfigWithoutWidgetId, WidgetMap } from "@/types/widget";

export interface WidgetState {
  widgets: WidgetMap;
  visualizationOrder: string[];
  widgetIndicates: {
    [baseId: string]: {
      counter: number;
      usedIndicates: number[];
    };
  };
}

export const widgetInitialState: WidgetState = {
  widgets: {},
  visualizationOrder: [],
  widgetIndicates: {}
};

export const widgetSlice = createSlice({
  name: "widget",
  initialState: widgetInitialState,
  reducers: {
    addWidget: (
      state,
      action: PayloadAction<{
        widget: WidgetConfigWithoutWidgetId;
        id: string;
      }>
    ) => {
      const { widget, id } = action.payload;

      if (!state.widgetIndicates[id]) {
        state.widgetIndicates[id] = { counter: 0, usedIndicates: [] };
      }

      state.widgetIndicates[id].counter += 1;
      const indicate = state.widgetIndicates[id].counter;
      state.widgetIndicates[id].usedIndicates.push(indicate);

      const widgetId = `${id}_${indicate}`;
      const newWidget = { ...widget, widget_id: widgetId };

      state.widgets[widgetId] = newWidget;

      if (!state.visualizationOrder.includes(widgetId)) {
        state.visualizationOrder = Array.isArray(state.visualizationOrder)
          ? [widgetId, ...state.visualizationOrder]
          : [widgetId];
      }
    },
    removeWidget: (state, action: PayloadAction<string>) => {
      const baseId = action.payload;
      const indicates = state.widgetIndicates[baseId];

      const idsToRemove = indicates?.usedIndicates.length
        ? indicates.usedIndicates.map((n) => `${baseId}_${n}`)
        : [baseId];

      idsToRemove.forEach((widgetId) => {
        delete state.widgets[widgetId];
      });

      state.visualizationOrder = state.visualizationOrder.filter(
        (id) => !idsToRemove.includes(id)
      );

      delete state.widgetIndicates[baseId];
    },
    updateWidgetH3Config: (
      state,
      action: PayloadAction<{
        widgetId: string;
        config: H3Configuration | null;
      }>
    ) => {
      const { widgetId: baseId, config } = action.payload;
      const indicates = state.widgetIndicates[baseId];

      if (indicates?.usedIndicates.length) {
        indicates.usedIndicates.forEach((n) => {
          const id = `${baseId}_${n}`;
          if (state.widgets[id]) state.widgets[id].h3Configuration = config;
        });
      } else if (state.widgets[baseId]) {
        state.widgets[baseId].h3Configuration = config;
      }
    },
    updateWidgetGroupBy: (
      state,
      action: PayloadAction<{ widgetId: string; groupBy: string }>
    ) => {
      const { widgetId: baseId, groupBy } = action.payload;
      const indicates = state.widgetIndicates[baseId];

      if (indicates?.usedIndicates.length) {
        indicates.usedIndicates.forEach((n) => {
          const id = `${baseId}_${n}`;
          if (state.widgets[id]) state.widgets[id].group_by = groupBy;
        });
      } else if (state.widgets[baseId]) {
        state.widgets[baseId].group_by = groupBy;
      }
    },
    resetWidgetState: () => widgetInitialState
  }
});

export const {
  addWidget,
  removeWidget,
  updateWidgetH3Config,
  updateWidgetGroupBy,
  resetWidgetState
} = widgetSlice.actions;

export const widgetReducer = widgetSlice.reducer;
